import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true, arrayName: '_subscriptions' })
@Component({
  selector: 'kt-select',
  templateUrl: './kt-select.component.html',
  styleUrl: './kt-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KtSelectComponent),
      multi: true,
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class KtSelectComponent implements ControlValueAccessor, OnInit {
  private static nextId = 0;
  readonly inputId = `ia-select-${KtSelectComponent.nextId++}`;
  onTouched: () => void = () => undefined;
  onChange: (_: any) => void = () => undefined;

  ktSelect: FormControl = new FormControl([]);

  @Input() items: any[];
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() placeholder: string;
  @Input() label: string;
  @Input() ariaLabel: string;
  @Input() hideSelected: boolean = false;
  @Input() virtualScroll: boolean = true;
  @Input() closeOnSelect: boolean = false;
  @Input() maxVisibleSelected: number = 5;
  @Input() appendTo: 'body';
  @Input() withoutBorder: boolean = false;
  @Input() singleRow: boolean = false;
  @Input() multiple: boolean = true;
  @Input() width: string;
  @Input() showColorDot: boolean = false;
  @Input() colorField: string = 'color';
  @Input() set disabled(isDisabled: boolean) {
    this.setDisabledState(isDisabled);
  }

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- Preserve the public (onClose) API used throughout the app.
  @Output() onClose: EventEmitter<any[]> = new EventEmitter();
  previousValue: unknown = [];

  ngOnInit() {
    this.ktSelect.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.onChange && this.onChange(value);
    });
  }

  toggleSelectAll() {
    if (this.ktSelect.disabled || !this.items?.length) return;
    const selectedValue = this.allSelected ? [] :
      (this.bindValue ? this.items.map(item => item[this.bindValue]) : this.items);
    this.ktSelect.setValue(selectedValue);
  }

  get allSelected(): boolean {
    return !!this.items?.length && Array.isArray(this.ktSelect.value) && this.ktSelect.value.length === this.items.length;
  }

  get partiallySelected(): boolean {
    return Array.isArray(this.ktSelect.value) && this.ktSelect.value.length > 0 && this.ktSelect.value.length < (this.items?.length || 0);
  }

  deselect(item) {
    if (item && !this.ktSelect.disabled) {
      const value = this.bindValue ? item[this.bindValue] : item;
      const newSelectedOptions = this.ktSelect.value?.filter((option) => option !== value);
      this.ktSelect.setValue(newSelectedOptions);
      this.valueChangeEmit();
    }
  }

  valueChangeEmit() {
    const value = this.ktSelect.value;
    if (!this.sameSelection(this.previousValue, value)) {
      this.previousValue = this.selectionSnapshot(value);
      this.onClose.emit(value);
    }
  }

  private selectionSnapshot(value: unknown): unknown {
    return Array.isArray(value) ? [...value] : value;
  }

  private sameSelection(previous: unknown, current: unknown): boolean {
    if (Array.isArray(previous) && Array.isArray(current)) {
      return previous.length === current.length && previous.every((value, index) => value === current[index]);
    }
    return previous === current;
  }

  onBlur() {
    this.onTouched();
  }

  writeValue(value): void {
    this.previousValue = this.selectionSnapshot(value);
    this.ktSelect.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.ktSelect.disable({ emitEvent: false });
    } else {
      this.ktSelect.enable({ emitEvent: false });
    }
  }
}
